// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getQuoteLineBreakPrice from "@bg/App/Components/RfqQuoteDetails/GetQuoteBreakPrice";
import QuoteLineImageCarousel from "@bg/App/Components/RfqQuoteDetails/QuoteLineImageCarousel";
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import deleteRelatedLine from "@bg/App/Handlers/RequestQuote/DeleteRelatedLine";
import updateLineInfo from "@bg/App/Handlers/RequestQuote/UpdateLineInfo";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { LinkPresentationProps } from "@bg/MobiusOverrides/Link/Link";
import ProductAvailability from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { productListLineStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import xScreen from "@bg/XDL/Utilities/xScreen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import RfqQuoteDetailsQuotedPricing from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    setGroupWishListLineIsSelected,
    deleteRelatedLine,
    updateLineInfo,
};

export interface RelatedQuoteLineStyles {
    quantityGridItem?: InjectableCss;
    quotedPricingGridItem?: InjectableCss;
    quoteButtonGrid?: InjectableCss;
    quoteLineButton?: ButtonPresentationProps;
    removeLine?: LinkPresentationProps;
    notesGridItem?: InjectableCss;
    addNotes?: LinkPresentationProps;
    textAreaField?: TextAreaProps;
    notesWrapper?: InjectableCss;
}

export const styles: RelatedQuoteLineStyles = {
    quantityGridItem: {
        css: css`
            min-width: calc((100% - 150px) / 3);
            text-align: center;
            @media (max-width: 600px) {
                width: 50%;
                text-align: left;
                order: 2;
            }
        `,
    },
    quotedPricingGridItem: {
        css: css`
            flex-grow: 1;
            @media (max-width: 600px) {
                width: 100%;
                order: 1;
            }
        `,
    },
    quoteButtonGrid: {
        css: css`
            min-width: 150px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            @media (max-width: 600px) {
                width: 50%;
                min-width: unset;
                order: 3;
            }
        `,
    },
    quoteLineButton: {
        css: css`
            &&&& {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                &&:hover {
                    background: ${getColor("common.white")};
                    color: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
            }
        `,
    },
    removeLine: linkIconStyleGenerator({
        icon: "Delete",
        textSize: "13px",
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    notesGridItem: {
        css: css`
            width: 100%;
            margin: 10px 0 0;
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                gap: 10px;
                align-items: flex-end;
            }
        `,
    },
    addNotes: linkIconStyleGenerator({
        icon: "Create",
        textSize: "13px",
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    textAreaField: {
        mergeCss: true,
        cssOverrides: {
            inputSelect: css`
                resize: none;
            `,
        },
    },
    notesWrapper: {
        css: css`
            text-align: right;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

interface OwnProps {
    quoteLine: QuoteLineModel;
    quote: QuoteModel;
    quoteLineClickHandler?: (line: QuoteLineModel) => void;
    groupName: string;
    parentLineId: string;
    isDisabled?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const RelatedQuoteLine: React.FC<Props> = ({
    quoteLine,
    quote,
    quoteLineClickHandler,
    groupName,
    deleteRelatedLine,
    parentLineId,
    isDisabled,
    updateLineInfo,
}) => {
    const [lineGroup, setLineGroup] = useState<any>();
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [lineQty, setLineQty] = useState(0);
    const [lineImages, setLineImages] = useState<any>();
    const [isDeleting, setIsDeleting] = useState(false);
    const [notes, setNotes] = useState(lineGroup?.notes);
    const [showNotes, setShowNotes] = useState(false);

    useEffect(() => {
        setNotes(lineGroup?.notes);
        setShowNotes(!!lineGroup?.notes);
    }, [lineGroup]);

    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = (e: any) => {
        if (e.currentTarget.value === lineGroup.notes) {
            return;
        }
        setShowNotes(!!e.currentTarget.value);
        updateLineInfo({
            cartId: quote.id,
            originalGroupName: groupName,
            linesModel: [
                {
                    lineId: quoteLine.id,
                    qtyOrdered: lineGroup.qtyOrdered,
                    sortOrder: lineGroup.sortOrder,
                    notes,
                    parentId: parentLineId,
                },
            ],
            reloadQuote: true,
        });
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Line Notes")
        : !lineGroup?.notes || lineGroup?.notes.length === 0
          ? translate("Add Line Notes")
          : translate("Show Line Notes");

    useEffect(() => {
        if (quoteLine && quoteLine.properties.groups) {
            setLineGroup(
                JSON.parse(quoteLine.properties.groups)?.filter(
                    (x: any) => x.groupName === groupName.toLowerCase() && x.parentId === parentLineId,
                )?.[0],
            );
        }
        if (quoteLine && quoteLine.properties) {
            let images = [] as any;
            if (quoteLine.properties.images) {
                images = JSON.parse(quoteLine.properties["images"]);
            } else {
                images = [{ id: quoteLine.productId, imagePath: quoteLine.smallImagePath }];
            }
            setLineImages(images);
        }
    }, [groupName, parentLineId, quoteLine]);

    useEffect(() => {
        if (quoteLine && lineGroup) {
            const lineQty =
                quoteLine.properties.isFixedQty === "true"
                    ? +quoteLine.properties.exactQty
                    : quoteLine.properties.minimumQty && +lineGroup?.qtyOrdered < +quoteLine.properties.minimumQty
                      ? +quoteLine.properties.minimumQty
                      : +lineGroup?.qtyOrdered;

            setLineQty(lineQty);
        }
    }, [quoteLine, lineGroup]);

    const deleteRelatedQuoteLine = () => {
        setIsDeleting(true);

        deleteRelatedLine({
            cartId: quote.id,
            originalGroupName: groupName,
            linesModel: [{ lineId: quoteLine.id, parentId: parentLineId }],
            reloadQuote: true,
            onComplete: result => {
                if (result.successful) {
                    setDeleteLineModalIsOpen(false);
                    setIsDeleting(false);
                }
            },
        });
    };

    const getUniqueGroupLineTotal = () => {
        let total = 0;
        total =
            (getQuoteLineBreakPrice(
                quoteLine.pricing?.actualBreakPrices ?? null,
                lineQty,
                quoteLine.pricing?.unitNetPrice,
            ) *
                lineQty *
                100) /
                100 || 0;
        return total;
    };

    const allowRemoveItems = quote.status === "QuoteCreated" || quote.status === "QuoteRequested";

    if (!quoteLine || !quote) {
        return null;
    }

    return (
        <StyledWrapper
            className="related-item"
            {...productListLineStyles.lineInnerContainer}
            data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
        >
            <StyledWrapper {...productListLineStyles.leftSection}>
                <StyledWrapper {...productListLineStyles.imageGridItem} className="related-item">
                    <QuoteLineImageCarousel
                        productId={quoteLine.id}
                        productImages={lineImages}
                        productUrl={quoteLine.productUri}
                    />
                </StyledWrapper>

                <StyledWrapper {...productListLineStyles.descriptionGridItem}>
                    <ProductDescription
                        product={quoteLine}
                        lines={2}
                        lineHeight={22}
                        extendedStyles={productListLineStyles.productDescriptionStyles}
                    />
                    <StyledWrapper {...productListLineStyles.partNumbersGridItem}>
                        <ProductPartNumbers
                            productNumber={quoteLine.properties.sku || quoteLine.erpNumber}
                            manufacturerItem={quoteLine.manufacturerItem}
                            extendedStyles={productListLineStyles.productPartNumbersStyles}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...productListLineStyles.availabilityGridItem}>
                        <ProductAvailability
                            productId={quoteLine.productId!}
                            availability={quoteLine.availability!}
                            unitOfMeasure={quoteLine.unitOfMeasure}
                            trackInventory={quoteLine.trackInventory}
                            extendedStyles={productListLineStyles.productAvailabilityStyles}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...productListLineStyles.rightSection}>
                <StyledWrapper {...productListLineStyles.rightSectionTop}>
                    <StyledWrapper {...productListLineStyles.priceAndAvailabilityInnerContainer}>
                        <StyledWrapper {...styles.quantityGridItem}>
                            <Typography weight={fontConfig.weight.semiBold}>
                                <legend>{translate("Qty")}</legend>
                                {lineQty}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.quotedPricingGridItem}>
                            <RfqQuoteDetailsQuotedPricing quote={quote} quoteLine={quoteLine} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.quoteButtonGrid}>
                            {quote.isEditable && (
                                <Button
                                    sizeVariant="small"
                                    {...styles.quoteLineButton}
                                    disabled={isDisabled}
                                    onClick={() => quoteLineClickHandler?.(quoteLine)}
                                >
                                    {translate("Quote")}
                                </Button>
                            )}
                            {!quote.isEditable && (
                                <Typography {...productListLineStyles.priceTotal}>
                                    <legend>{translate("Total")}</legend>
                                    <LocalizedCurrency amount={getUniqueGroupLineTotal()} />
                                </Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {quoteLine.properties["pricingFailed"]?.toLowerCase() === "true" && (
                    <StyledWrapper>
                        <Typography
                            color="danger"
                            size={fontConfig.fontSize.xSmall}
                            lineHeight={fontConfig.lineHeight.singleLineSmall}
                        >
                            {siteMessage("Custom_Price_FailedToLoad")}
                        </Typography>
                    </StyledWrapper>
                )}
                {quote.isEditable && !isDisabled && (
                    <StyledWrapper {...styles.notesGridItem}>
                        {allowRemoveItems && (
                            <Link onClick={() => setDeleteLineModalIsOpen(true)} {...styles.removeLine}>
                                {translate("Remove Item")}
                            </Link>
                        )}
                        <StyledWrapper {...styles.notesWrapper}>
                            <Link {...styles.addNotes} className="notes-link" onClick={toggleNotesClickHandler}>
                                {toggleNotesHeading}
                            </Link>
                            {showNotes && (
                                <TextArea
                                    value={notes}
                                    rows={3}
                                    {...styles.textAreaField}
                                    disabled={!quote.canModifyOrder}
                                    onChange={notesChangeHandler}
                                    onBlur={notesBlurHandler}
                                    maxLength={300}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={deleteRelatedQuoteLine}
                submitTestSelector="delteRelatedQuoteLine"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(RelatedQuoteLine);
